/* eslint-disable jsx-a11y/anchor-is-valid */
import {useState, useEffect, useCallback} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {useFormik} from 'formik'
import {getUserByToken, login, googleSignIn, sendPhoneCode, verifyPhoneCode, facebookSignIn} from '../core/_requests'
import {useAuth} from '../core/Auth'
import {useIntl} from 'react-intl'

// Google OAuth Client ID
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '266985490329-3imd6ndlah8s68ko95eucubleneg6goi.apps.googleusercontent.com'
// Facebook App ID — the "Login with Facebook" button only appears once this is
// set (create a free app at developers.facebook.com, then set REACT_APP_FB_APP_ID
// + FB_APP_SECRET on the API). Mirrors how the Google button is gated.
// "Login with Facebook" is switched OFF for now (doesn't work yet). To turn it back on:
// const FB_APP_ID = process.env.REACT_APP_FB_APP_ID || ''
const FB_APP_ID = ''

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: any) => void
          renderButton: (element: HTMLElement, options: any) => void
          prompt: () => void
        }
      }
    }
    FB?: any
    fbAsyncInit?: () => void
  }
}

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .min(2, 'מינימום 2 תווים')
    .max(50, 'מקסימום 50 תווים')
    .required('שדה חובה'),
  password: Yup.string()
    .min(3, 'מינימום 3 תווים')
    .max(50, 'מקסימום 50 תווים')
    .required('שדה חובה'),
})

const initialValues = {
  email: '',
  password: '',
}

// Basic client-side sanity check for an Israeli phone. The server does the real
// (authoritative) normalization/validation — this is just to avoid empty sends.
function looksLikePhone(v: string) {
  const digits = (v || '').replace(/\D/g, '')
  return digits.length >= 9 && digits.length <= 15
}

/*
  Formik+YUP+Typescript:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
*/

export function Login() {
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const {saveAuth, setCurrentUser} = useAuth()
  const intl = useIntl()

  // 'password' = email+password form, 'phone' = SMS OTP flow.
  const [mode, setMode] = useState<'password' | 'phone'>('password')
  // Phone OTP flow state.
  const [phoneStep, setPhoneStep] = useState<'enter' | 'code'>('enter')
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [phoneLoading, setPhoneLoading] = useState(false)
  const [phoneError, setPhoneError] = useState('')
  const [phoneInfo, setPhoneInfo] = useState('')
  const [resendIn, setResendIn] = useState(0)
  const [fbLoading, setFbLoading] = useState(false)
  const [fbError, setFbError] = useState('')

  const finishLogin = useCallback(async (api_token: string) => {
    saveAuth({api_token})
    const {data: user} = await getUserByToken(api_token)
    setCurrentUser(user)
  }, [saveAuth, setCurrentUser])

  // Handle Google Sign-In callback
  const handleGoogleCallback = useCallback(async (response: any) => {
    setGoogleLoading(true)
    try {
      const {data} = await googleSignIn(response.credential)
      if (data.success && data.api_token) {
        await finishLogin(data.api_token)
      } else {
        formik.setStatus(data.error || 'שגיאה בהתחברות עם Google')
      }
    } catch (error: any) {
      console.error(error)
      saveAuth(undefined)
      formik.setStatus(error.response?.data?.error || 'שגיאה בהתחברות עם Google')
    } finally {
      setGoogleLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finishLogin, saveAuth])

  // Initialize Google Sign-In
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) {
      return
    }

    const script = document.createElement('script')
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.defer = true
    document.body.appendChild(script)

    script.onload = () => {
      if (window.google) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleCallback,
        })
        const googleBtn = document.getElementById('google-signin-button')
        if (googleBtn) {
          window.google.accounts.id.renderButton(googleBtn, {
            theme: 'outline',
            size: 'large',
            width: '100%',
            text: 'signin_with',
            locale: 'he',
          })
        }
      }
    }

    return () => {
      const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]')
      if (existingScript) {
        existingScript.remove()
      }
    }
  }, [handleGoogleCallback])

  // Load the Facebook JS SDK (only when a FB app id is configured).
  useEffect(() => {
    if (!FB_APP_ID) return
    if (document.getElementById('facebook-jssdk')) return

    window.fbAsyncInit = function () {
      window.FB.init({
        appId: FB_APP_ID,
        cookie: true,
        xfbml: false,
        version: 'v19.0',
      })
    }
    const script = document.createElement('script')
    script.id = 'facebook-jssdk'
    script.src = 'https://connect.facebook.net/en_US/sdk.js'
    script.async = true
    script.defer = true
    document.body.appendChild(script)
  }, [])

  // Resend cooldown ticker for the phone code step.
  useEffect(() => {
    if (resendIn <= 0) return
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000)
    return () => clearTimeout(t)
  }, [resendIn])

  const handleFacebookLogin = useCallback(() => {
    setFbError('')
    if (!window.FB) {
      setFbError('פייסבוק עדיין נטען, נסה שוב בעוד רגע')
      return
    }
    setFbLoading(true)
    window.FB.login(
      async (response: any) => {
        try {
          if (response.status === 'connected' && response.authResponse?.accessToken) {
            const {data} = await facebookSignIn(response.authResponse.accessToken)
            if (data.success && data.api_token) {
              await finishLogin(data.api_token)
            } else {
              setFbError(data.error || 'שגיאה בהתחברות עם פייסבוק')
            }
          } else {
            setFbError('ההתחברות עם פייסבוק בוטלה')
          }
        } catch (error: any) {
          console.error(error)
          saveAuth(undefined)
          setFbError(error.response?.data?.error || 'שגיאה בהתחברות עם פייסבוק')
        } finally {
          setFbLoading(false)
        }
      },
      {scope: 'email'}
    )
  }, [finishLogin, saveAuth])

  const handleSendCode = useCallback(async () => {
    setPhoneError('')
    setPhoneInfo('')
    if (!looksLikePhone(phone)) {
      setPhoneError('נא להזין מספר טלפון תקין')
      return
    }
    setPhoneLoading(true)
    try {
      const {data} = await sendPhoneCode(phone.trim())
      if (data.ok) {
        setPhoneStep('code')
        setPhoneInfo('אם המספר משויך לחשבון פעיל, נשלח אליו קוד ב-SMS')
        setResendIn(30)
      } else {
        setPhoneError(data.message || 'מספר טלפון לא תקין')
      }
    } catch (error: any) {
      setPhoneError(error.response?.data?.message || 'שגיאה בשליחת הקוד')
    } finally {
      setPhoneLoading(false)
    }
  }, [phone])

  const handleVerifyCode = useCallback(async () => {
    setPhoneError('')
    if (!/^\d{4,8}$/.test(code.trim())) {
      setPhoneError('נא להזין את הקוד שקיבלת')
      return
    }
    setPhoneLoading(true)
    try {
      const {data} = await verifyPhoneCode(phone.trim(), code.trim())
      if (data.ok && data.api_token) {
        await finishLogin(data.api_token)
      } else {
        setPhoneError(data.message || 'קוד שגוי')
      }
    } catch (error: any) {
      saveAuth(undefined)
      setPhoneError(error.response?.data?.message || 'קוד שגוי או שפג תוקפו')
    } finally {
      setPhoneLoading(false)
    }
  }, [phone, code, finishLogin, saveAuth])

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      try {
        const {data: auth} = await login(values.email, values.password)
        saveAuth(auth)
        const {data: user} = await getUserByToken(auth.api_token)
        setCurrentUser(user)
      } catch (error) {
        console.error(error)
        saveAuth(undefined)
        setStatus(intl.formatMessage({id: 'AUTH.GENERAL.WRONGLOGIN'}))
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  return (
    <form
      className='form w-100'
      onSubmit={formik.handleSubmit}
      noValidate
      id='kt_login_signin_form'
    >
      {/* begin::Heading */}
      <div className='text-center mb-11'>
        <h1 className='text-dark fw-bolder mb-3'>{intl.formatMessage({id: 'AUTH.GENERAL.SIGNIN'})}</h1>
      </div>
      {/* end::Heading */}

      {/* begin::Social sign-in (Google + Facebook) */}
      {(GOOGLE_CLIENT_ID || FB_APP_ID) && (
        <>
          <div className='mb-6'>
            {GOOGLE_CLIENT_ID && (
              <div className='mb-3'>
                <div id="google-signin-button" className='d-flex justify-content-center'></div>
                {googleLoading && (
                  <div className='text-center mt-3'>
                    <span className='spinner-border spinner-border-sm'></span>
                    <span className='ms-2'>מתחבר...</span>
                  </div>
                )}
              </div>
            )}

            {FB_APP_ID && (
              <>
                <button
                  type='button'
                  onClick={handleFacebookLogin}
                  disabled={fbLoading}
                  className='btn btn-flex btn-outline w-100 d-flex align-items-center justify-content-center'
                  style={{borderColor: '#1877F2', color: '#1877F2'}}
                >
                  <svg width='20' height='20' viewBox='0 0 24 24' fill='#1877F2' className='me-2'>
                    <path d='M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z' />
                  </svg>
                  {fbLoading ? 'מתחבר...' : 'התחברות עם פייסבוק'}
                </button>
                {fbError && (
                  <div className='text-danger text-center mt-2 fs-7'>{fbError}</div>
                )}
              </>
            )}
          </div>
          {/* begin::Separator */}
          <div className='separator separator-content my-8'>
            <span className='w-175px text-gray-500 fw-semibold fs-7'>או</span>
          </div>
          {/* end::Separator */}
        </>
      )}
      {/* end::Social sign-in */}

      {/* begin::Method toggle (email/password vs phone) */}
      <div className='d-flex mb-8'>
        <button
          type='button'
          className={clsx('btn btn-sm flex-grow-1 me-2', mode === 'password' ? 'btn-primary' : 'btn-light')}
          onClick={() => setMode('password')}
        >
          שם משתמש וסיסמה
        </button>
        <button
          type='button'
          className={clsx('btn btn-sm flex-grow-1', mode === 'phone' ? 'btn-primary' : 'btn-light')}
          onClick={() => {
            setMode('phone')
            setPhoneStep('enter')
            setPhoneError('')
            setPhoneInfo('')
          }}
        >
          כניסה עם טלפון
        </button>
      </div>
      {/* end::Method toggle */}

      {mode === 'password' && (
        <>
          {formik.status && (
            <div className='mb-lg-15 alert alert-danger'>
              <div className='alert-text font-weight-bold'>{formik.status}</div>
            </div>
          )}

          {/* begin::Form group */}
          <div className='fv-row mb-8'>
            <label className='form-label fs-6 fw-bolder text-dark'>שם משתמש</label>
            <input
              placeholder='שם משתמש'
              {...formik.getFieldProps('email')}
              className={clsx(
                'form-control bg-transparent',
                {'is-invalid': formik.touched.email && formik.errors.email},
                {'is-valid': formik.touched.email && !formik.errors.email}
              )}
              type='text'
              name='email'
              autoComplete='off'
            />
            {formik.touched.email && formik.errors.email && (
              <div className='fv-plugins-message-container'>
                <span role='alert'>{formik.errors.email}</span>
              </div>
            )}
          </div>
          {/* end::Form group */}

          {/* begin::Form group */}
          <div className='fv-row mb-3'>
            <label className='form-label fw-bolder text-dark fs-6 mb-0'>{intl.formatMessage({id: 'AUTH.GENERAL.PASSWORD'})}</label>
            <input
              type='password'
              placeholder='סיסמה'
              autoComplete='off'
              {...formik.getFieldProps('password')}
              className={clsx(
                'form-control bg-transparent',
                {'is-invalid': formik.touched.password && formik.errors.password},
                {'is-valid': formik.touched.password && !formik.errors.password}
              )}
            />
            {formik.touched.password && formik.errors.password && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.password}</span>
                </div>
              </div>
            )}
          </div>
          {/* end::Form group */}

          {/* begin::Action */}
          <div className='d-grid mb-10 mt-8'>
            <button
              type='submit'
              id='kt_sign_in_submit'
              className='btn btn-primary'
              disabled={formik.isSubmitting || !formik.isValid}
            >
              {!loading && <span className='indicator-label'>{intl.formatMessage({id: 'AUTH.GENERAL.CONTINUE'})}</span>}
              {loading && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  Please wait...
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
          {/* end::Action */}
        </>
      )}

      {mode === 'phone' && (
        <div>
          {phoneError && (
            <div className='mb-6 alert alert-danger'>
              <div className='alert-text font-weight-bold'>{phoneError}</div>
            </div>
          )}
          {phoneInfo && !phoneError && (
            <div className='mb-6 alert alert-primary'>
              <div className='alert-text'>{phoneInfo}</div>
            </div>
          )}

          {phoneStep === 'enter' && (
            <>
              <div className='fv-row mb-8'>
                <label className='form-label fs-6 fw-bolder text-dark'>מספר טלפון</label>
                <input
                  placeholder='050-0000000'
                  className='form-control bg-transparent'
                  type='tel'
                  inputMode='tel'
                  dir='ltr'
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  autoComplete='tel'
                />
              </div>
              <div className='d-grid mb-10'>
                <button
                  type='button'
                  className='btn btn-primary'
                  onClick={handleSendCode}
                  disabled={phoneLoading}
                >
                  {!phoneLoading && <span className='indicator-label'>שלח קוד ב-SMS</span>}
                  {phoneLoading && (
                    <span className='indicator-progress' style={{display: 'block'}}>
                      שולח...
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </button>
              </div>
            </>
          )}

          {phoneStep === 'code' && (
            <>
              <div className='fv-row mb-8'>
                <label className='form-label fs-6 fw-bolder text-dark'>הזן את הקוד שקיבלת</label>
                <input
                  placeholder='______'
                  className='form-control bg-transparent text-center fs-2 tracking-widest'
                  type='text'
                  inputMode='numeric'
                  maxLength={8}
                  dir='ltr'
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  autoComplete='one-time-code'
                  autoFocus
                />
              </div>
              <div className='d-grid mb-4'>
                <button
                  type='button'
                  className='btn btn-primary'
                  onClick={handleVerifyCode}
                  disabled={phoneLoading}
                >
                  {!phoneLoading && <span className='indicator-label'>התחבר</span>}
                  {phoneLoading && (
                    <span className='indicator-progress' style={{display: 'block'}}>
                      מאמת...
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </button>
              </div>
              <div className='d-flex justify-content-between align-items-center mb-10'>
                <button
                  type='button'
                  className='btn btn-link p-0'
                  onClick={() => {
                    setPhoneStep('enter')
                    setCode('')
                    setPhoneError('')
                    setPhoneInfo('')
                  }}
                >
                  שנה מספר
                </button>
                <button
                  type='button'
                  className='btn btn-link p-0'
                  disabled={resendIn > 0 || phoneLoading}
                  onClick={handleSendCode}
                >
                  {resendIn > 0 ? `שלח שוב בעוד ${resendIn}s` : 'שלח קוד שוב'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </form>
  )
}
